
/* ============================== src/components/discussion/CommentItem.jsx ============================== */
// src/components/discussion/CommentItem.jsx
import { useRef, useMemo, useCallback } from "react";
import EmployeeCardLink from "../EmployeeCardLink";
import { fmtDT } from "../../utils/date";
import { renderTextWithLinks } from "./render";
import { useDiscussion } from "./context";
import { CommentPolicy } from "./CommentPolicy";
import CommentEditor from "./CommentEditor";
import CommentReactions from "./CommentReactions";
import CommentAttachments from "./CommentAttachments";
import CommentActions from "./CommentActions";
import ReactionPicker from "./ReactionPicker";
import { useReactionPicker } from "../../hooks";
import { useToast } from "../../context/ToastContext";
import { copyToClipboard } from "../../utils/clipboard";

export default function CommentItem({ comment, depth, children }) {
  const {
    currentUser,
    getAuthor,
    editingId,
    searchQuery,
    showTaskLink,
    onTaskClick,
    tasks,
    setReaction,
    isCommentUnread,
  } = useDiscussion();

  const { showToast } = useToast();
  const rootRef = useRef(null);
  const picker = useReactionPicker(rootRef);

  const author = getAuthor(comment.authorId);
  const task = comment.taskId && tasks?.find((t) => t.id === comment.taskId);
  const myReaction = CommentPolicy.getUserReaction(comment, currentUser.id);
  const isUnread = isCommentUnread(comment);

  const handlePathClick = useCallback(
    async (path) => {
      const ok = await copyToClipboard(path);
      showToast(
        ok ? "Путь скопирован" : "Не удалось скопировать путь",
        ok ? "success" : "warning",
      );
    },
    [showToast],
  );

  const textContent = useMemo(
    () =>
      renderTextWithLinks(comment.text, {
        searchQuery,
        onPathClick: handlePathClick,
      }),
    [comment.text, searchQuery, handlePathClick],
  );

  const pick = (emoji) => {
    setReaction(comment.id, emoji);
    picker.close();
  };

  const handleTriggerClick = (e) => {
    e.stopPropagation();
    picker.toggle();
  };

  const wrapperClass = "cm-item" + (depth > 0 ? " cm-item--nested" : "");
  const cmClass =
    "cm" +
    (depth > 0 ? " reply" : "") +
    (comment.pinned ? " pinned" : "") +
    (isUnread ? " unread" : "");

  return (
    <div id={`comment-${comment.id}`} ref={rootRef} className={wrapperClass}>
      <div className={cmClass}>
        <div className="cm-head">
          <EmployeeCardLink employee={author} size="xs" />
          <span className="cm-author">
            {author ? `${author.last} ${author.first}` : "-"}
          </span>
          <span className="mut sm">{fmtDT(comment.createdAt)}</span>
          {comment.updatedAt > comment.createdAt && (
            <span className="mut sm">(ред.)</span>
          )}
          {comment.pinned && (
            <span className="pinned-badge" title="Закреплено">
              📌
            </span>
          )}
          {showTaskLink && task && onTaskClick && (
            <button
              className="link"
              onClick={(e) => {
                e.stopPropagation();
                onTaskClick(comment.taskId);
              }}
              title="Открыть задачу"
            >
              {task.title}
            </button>
          )}
        </div>

        {editingId === comment.id ? (
          <CommentEditor comment={comment} />
        ) : (
          <div className="cm-text">{textContent}</div>
        )}

        <CommentAttachments attachments={comment.attachments || []} />

        <div className="cm-foot-row">
          <CommentActions comment={comment} />
          <div className="cm-reactions-bar">
            <button
              type="button"
              className="cm-reaction-trigger"
              onClick={handleTriggerClick}
              title="Реакция"
              aria-label="Реакция"
            >
              Реакция
            </button>
            <CommentReactions comment={comment} />
            {picker.open && (
              <ReactionPicker
                containerRef={picker.pickerRef}
                activeEmoji={myReaction}
                onPick={pick}
              />
            )}
          </div>
        </div>
      </div>
      {children}
    </div>
  );
}
